/**
 * `featured_product` — a single-product spotlight (module 22 D3).
 *
 * Merchant-curated copy + image + CTA on a light surface card, distinct from
 * the dark `promo_banner`. The price line is free text (the seller's own
 * claim), never a computed figure — this section carries no catalog data.
 * Class-free (see `index.ts`).
 */
import type { LinkTarget } from "@flint/api-contract/store-home";
import { resolveTarget, isExternal, type TargetContext } from "./resolve-target.js";
import { SectionImage } from "./section-image.js";

export function FeaturedProduct({
  eyebrow,
  headline,
  body,
  priceLabel,
  imageUrl,
  cta,
  ctx,
}: {
  eyebrow?: string | undefined;
  headline: string;
  body?: string | undefined;
  priceLabel?: string | undefined;
  imageUrl?: string | undefined;
  cta?: { label: string; target: LinkTarget } | undefined;
  ctx: TargetContext;
}) {
  const href = cta ? resolveTarget(cta.target, ctx) : null;

  return (
    <section
      aria-label={headline}
      className="flint-feature"
      style={{
        backgroundColor: "var(--paper)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        overflow: "hidden",
      }}
    >
      <div className="flint-feature-media">
        <SectionImage src={imageUrl ?? null} alt="" aspectRatio="4 / 3" radius="0" />
      </div>
      <div className="flint-feature-text">
        {eyebrow ? (
          <p
            style={{
              color: "var(--brand)",
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: "0.5rem",
            }}
          >
            {eyebrow}
          </p>
        ) : null}
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: "1.75rem",
            lineHeight: 1.15,
            color: "var(--ink)",
            margin: 0,
          }}
        >
          {headline}
        </h2>
        {body ? (
          <p style={{ marginTop: "0.75rem", fontSize: "0.9375rem", color: "var(--muted)" }}>{body}</p>
        ) : null}
        {priceLabel ? (
          <p
            style={{
              marginTop: "1rem",
              fontFamily: "var(--font-display)",
              fontWeight: 600,
              fontSize: "1.25rem",
              color: "var(--ink)",
            }}
          >
            {priceLabel}
          </p>
        ) : null}
        {cta && href ? (
          <a
            href={href}
            {...(isExternal(cta.target) ? { rel: "noopener noreferrer", target: "_blank" } : {})}
            style={{
              display: "inline-flex",
              marginTop: "1.5rem",
              padding: "0.625rem 1.5rem",
              borderRadius: "var(--r-sm)",
              backgroundColor: "var(--accent)",
              color: "var(--accent-fg)",
              fontWeight: 600,
              fontSize: "0.9375rem",
              textDecoration: "none",
            }}
          >
            {cta.label}
          </a>
        ) : null}
      </div>
    </section>
  );
}
